// NOTE: This file is responsible for rasterizing a new PDF visual, where we render fields values
//   like text onto a PDF. This is separate from previewing the PDF or previewing text on a PDF using div elements.
//   This logic shares the same logic as the backend and replicates what PDFs will actually look like after rasterizing all content into a net new PDF.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { calculateOptimalLayout, createPdfTextMeasurer } from "./textLayout";
import type { PdfFieldRect, PdfFieldSample, PdfNormalizedRect, PdfSource } from "./types";
import { PdfDocumentViewer } from "./PdfDocumentViewer";

interface PdfSamplePreviewProps {
  source: PdfSource | string | null;
  fields: PdfFieldRect[];
  samples?: PdfFieldSample[] | Record<string, string | undefined>;
  width?: number;
  page?: number;
  defaultPage?: number;
  onPageChange?: (page: number) => void;
  throttleMs?: number;
  showToolbar?: boolean;
  className?: string;
  onDocumentLoad?: (metrics: { totalPages: number }) => void;
  renderOverlay?: Parameters<typeof PdfDocumentViewer>[0]["renderOverlay"];
}

const MIN_FONT_SIZE = 6;
const LINE_HEIGHT_MULTIPLIER = 1.15;

function toSampleMap(samples?: PdfFieldSample[] | Record<string, string | undefined>) {
  if (!samples) {
    return new Map<string, string>();
  }

  if (Array.isArray(samples)) {
    return new Map(samples.map((sample) => [sample.id, sample.value ?? ""]));
  }

  return new Map(
    Object.entries(samples).map(([key, value]) => [key, value ?? ""]),
  );
}

async function toUint8Array(source: PdfSource | string | null) {
  if (!source) {
    return null;
  }

  if (typeof source === "string") {
    const response = await fetch(source);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  if (source instanceof File) {
    const buffer = await source.arrayBuffer();
    return new Uint8Array(buffer);
  }

  if (source instanceof Uint8Array) {
    return source;
  }

  return new Uint8Array(source);
}

async function renderPdfWithSamples(
  sourceBytes: Uint8Array,
  fields: PdfFieldRect[],
  samples: Map<string, string>,
) {
  if (!sourceBytes) {
    return null;
  }

  const pdfDoc = await PDFDocument.load(sourceBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();
  const measurer = createPdfTextMeasurer(font);

  fields.forEach((field) => {
    const value = samples.get(field.id);
    if (!value) {
      return;
    }

    const pageIndex = field.page - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) {
      return;
    }

    const page = pages[pageIndex];
    const { width: pageWidth, height: pageHeight } = page.getSize();

    const rect: PdfNormalizedRect = field.rect;
    const x = rect.x * pageWidth;
    const y = rect.y * pageHeight;
    const width = rect.width * pageWidth;
    const height = rect.height * pageHeight;

    const baseFontSize = Math.max(Math.round(height * 0.45), 8);
    const layout = calculateOptimalLayout({
      text: value,
      measurer,
      initialFontSize: baseFontSize,
      containerWidth: width,
      containerHeight: height,
      containerY: y,
      minFontSize: MIN_FONT_SIZE,
      lineHeightMultiplier: LINE_HEIGHT_MULTIPLIER,
    });

    let cursorY = y + height - layout.lineHeight;

    layout.lines.forEach((line) => {
      page.drawText(line.text, {
        x,
        y: cursorY,
        size: layout.fontSize,
        font,
      });
      cursorY -= layout.lineHeight;
    });
  });

  return pdfDoc.save();
}

export function PdfSamplePreview({
  source,
  fields,
  samples,
  width,
  page,
  defaultPage,
  onPageChange,
  throttleMs = 1000,
  showToolbar,
  className,
  onDocumentLoad,
  renderOverlay,
}: PdfSamplePreviewProps) {
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const lastRenderRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRenderingRef = useRef(false);
  const pendingSignatureRef = useRef<string | null>(null);

  const sampleMap = useMemo(() => toSampleMap(samples), [samples]);

  const signature = useMemo(() => {
    const fieldSignature = fields
      .map(
        (field) =>
          `${field.id}:${field.page}:${field.rect.x}:${field.rect.y}:${field.rect.width}:${field.rect.height}`,
      )
      .join("|");

    const sampleSignature = Array.from(sampleMap.entries())
      .map(([id, value]) => `${id}:${value ?? ""}`)
      .join("|");

    return `${fieldSignature}__${sampleSignature}`;
  }, [fields, sampleMap]);

  const performRender = useCallback(async () => {
    if (isRenderingRef.current) {
      return;
    }

    if (!source) {
      setPreviewBytes(null);
      return;
    }

    if (fields.length === 0 || sampleMap.size === 0) {
      const fallbackBytes = await toUint8Array(source);
      setPreviewBytes(fallbackBytes);
      return;
    }

    isRenderingRef.current = true;
    setIsRendering(true);
    try {
      const originalBytes = await toUint8Array(source);
      if (!originalBytes) {
        setPreviewBytes(null);
        return;
      }

      const renderedBytes = await renderPdfWithSamples(originalBytes, fields, sampleMap);
      setPreviewBytes(renderedBytes ? new Uint8Array(renderedBytes) : originalBytes);
    } catch (error) {
      console.error("Failed to render PDF preview", error);
      const fallbackBytes = await toUint8Array(source);
      setPreviewBytes(fallbackBytes);
    } finally {
      setIsRendering(false);
      isRenderingRef.current = false;
      lastRenderRef.current = Date.now();
    }
  }, [fields, sampleMap, source]);

  useEffect(() => {
    if (!source) {
      setPreviewBytes(null);
      return;
    }

    pendingSignatureRef.current = signature;

    const schedule = () => {
      if (!pendingSignatureRef.current) {
        return;
      }

      if (isRenderingRef.current) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(schedule, throttleMs);
        return;
      }

      const elapsed = Date.now() - lastRenderRef.current;
      if (elapsed < throttleMs) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(schedule, throttleMs - elapsed);
        return;
      }

      pendingSignatureRef.current = null;
      performRender();
    };

    schedule();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [performRender, source, signature, throttleMs]);

  const effectiveSource = previewBytes ?? source;

  return (
    <PdfDocumentViewer
      source={effectiveSource}
      width={width}
      page={page}
      defaultPage={defaultPage}
      onPageChange={onPageChange}
      showToolbar={showToolbar}
      className={className}
      onDocumentLoad={onDocumentLoad}
      renderOverlay={renderOverlay}
      loadingFallback={
        isRendering ? (
          <div className="p-4 text-center text-sm text-muted-foreground">Rendering preview…</div>
        ) : undefined
      }
    />
  );
}

